import { useEffect, useState } from "react";
import { api } from "./api";
import { X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const inputCls =
  "w-full px-4 py-3 rounded-md border border-slate-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#0F4C2A]/40 focus:border-[#0F4C2A]";

export const Btn = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 rounded-md font-bold text-sm uppercase tracking-wide transition-colors px-5 py-3 disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

export const PrimaryBtn = ({ className = "", ...p }) => (
  <Btn {...p} className={`bg-[#0F4C2A] text-white hover:bg-[#0A3823] shadow-md ${className}`} />
);

export const GoldBtn = ({ className = "", ...p }) => (
  <Btn {...p} className={`bg-[#D97706] text-white hover:bg-[#F59E0B] shadow-md ${className}`} />
);

export const DangerBtn = ({ className = "", ...p }) => (
  <Btn {...p} className={`bg-red-700 text-white hover:bg-red-800 ${className}`} />
);

export const GhostBtn = ({ className = "", ...p }) => (
  <Btn {...p} className={`bg-white border border-slate-300 text-slate-700 hover:border-slate-400 ${className}`} />
);

export const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-bold text-slate-700 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6" onClick={onClose} data-testid={testid}>
    <div className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-slate-200 z-10">
        <h3 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A]">{title}</h3>
        <button onClick={onClose} aria-label="Close" className="p-2 text-slate-500 hover:text-slate-800" data-testid={`${testid}-close`}>
          <X size={22} />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

export const ConfirmDialog = ({ message, onConfirm, onCancel, testid }) => (
  <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-6" data-testid={testid}>
    <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
      <h3 className="font-condensed text-2xl font-bold uppercase text-slate-900">Are you sure?</h3>
      <p className="text-slate-600 mt-2 text-sm leading-relaxed">{message}</p>
      <div className="flex gap-3 mt-6">
        <DangerBtn className="flex-1" onClick={onConfirm} data-testid={`${testid}-confirm`}>
          Yes, Delete
        </DangerBtn>
        <GhostBtn className="flex-1" onClick={onCancel} data-testid={`${testid}-cancel`}>
          Cancel
        </GhostBtn>
      </div>
    </div>
  </div>
);

export const ImagePicker = ({ value, onChange, label = "Photo", testid }) => {
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      const url = r.data.url;
      // Confirm the image actually loads from permanent storage before saying success.
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      onChange(url);
      toast.success("Photo uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. The photo was not saved — please try again.");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-1.5">{label}</label>
      {value ? (
        <img src={value} alt={label} className="w-full h-44 object-cover rounded-md border border-slate-200 mb-3" data-testid={`${testid}-preview`} />
      ) : (
        <div className="w-full h-44 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-sm mb-3" data-testid={`${testid}-empty`}>
          No photo yet
        </div>
      )}
      <label
        className={`inline-flex items-center gap-2 bg-[#0F4C2A] text-white px-5 py-3 rounded-md font-bold text-sm uppercase tracking-wide cursor-pointer hover:bg-[#0A3823] ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        data-testid={`${testid}-upload`}
      >
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        {uploading ? "Uploading..." : value ? "Replace Photo" : "Upload Photo"}
        <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-file-input`} />
      </label>
    </div>
  );
};

export const useResource = (name) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/${name}`);
      setItems(r.data);
    } catch {
      toast.error(`Could not load ${name}. Please try again.`);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return { items, setItems, loading, reload: load };
};

export const useContentDoc = () => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/admin/content")
      .then((r) => setContent(r.data))
      .catch(() => toast.error("Could not load website content."));
  }, []);

  const save = async (next) => {
    await api.put("/admin/content", next);
    setContent(next);
  };

  return { content, setContent, save };
};

export const SectionHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
    <div>
      <h1 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A]">{title}</h1>
      {subtitle && <p className="text-slate-600 mt-1 text-sm sm:text-base">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Loading = () => (
  <div className="flex items-center justify-center py-20 text-slate-500">
    <Loader2 size={26} className="animate-spin mr-3" /> Loading...
  </div>
);
